import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { makeStyles } from "tss-react/mui";
//All Loading related components
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

import DatasourceClass from "../../service/dataSource";
import DQMClass from "../../service/dqm";
import CommonLayout from "../Common/Layout";
import FieldsTable from "./FieldsTable";

import ComponentSuspense from "../Common/ComponentSuspense";
import HelpContextIcon from "../Common/HelpPanel/HelpContextIcon";
import { DATA_SOURCE } from "../../util/Data/DataModeling";
import { ButtonUI, KMKBackButton } from "../Common";
import { navDatasets } from "../kmk/AppData";
import FindingsChart from "./FindingsChart";
import { convertTimezone } from "../../util/common";
const CoverageChart = React.lazy(() => import("./CoverageChart"));
const HealthChart = React.lazy(() => import("./HealthChart"));

const useStyles = makeStyles()((theme) => ({
  boxTotalRecords: {
    display: "flex",
    alignItems: "center",
    justifyContent: "start",
    border: "1px solid #a6a6a6",
    padding: "0 8px 0 8px",
    minHeight: "36px",
    borderRadius: 4,
    "& span": {
      color: "#777",
      marginLeft: "10px",
    },
  },
  ifDisabled: {
    "& .Mui-disabled": {
      cursor: "not-allowed",
    },
  },
}));

const DQM = () => {
  const workspace = useSelector((state) => state.workspace);
  const { Name: workspaceName, accessRole, IsDataVaultEnabled } = workspace.workspace;

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const defaultDs = useMemo(
    () => searchParams?.get("name") || location?.state?.Name,
    [location?.state?.Name, searchParams]
  );

  const [datasources, setDatasources] = useState([]);
  const [datasource, setDatasource] = useState("");
  const [recordDate, setRecordDate] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [findingsCounts, setFindingsCounts] = useState({
    totalFindings: 0,
    criticalCount: 0,
    warningCount: 0,
  });

  const [selectedHistory, setSelectedHistory] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingFields, setLoadingFields] = useState(true);

  const { Workspace_id } = useParams();
  const navigate = useNavigate();
  const { classes } = useStyles();

  const childRef = useRef();

  const navigateToHome = useCallback(() => navigate(`/my-workspaces/${Workspace_id}`), [Workspace_id, navigate]);

  const getFindingsCounts = useCallback(
    (counts, totalFields) => setFindingsCounts({ totalFindings: totalFields * totalCount, ...counts }),
    [totalCount]
  );

  const handlePointerClick = useCallback((jobHistory_id) => setSelectedHistory(jobHistory_id), []);

  const handleDatasetChange = useCallback(
    (e) => {
      childRef?.current?.setFields([]);
      setFindingsCounts({ totalFindings: 0, criticalCount: 0, warningCount: 0 });
      setLoadingFields(true);
      setSelectedHistory(null);
      setDatasource(datasources.find(({ name }) => name === e.target.value));
    },
    [datasources]
  );

  const getTotalRecordsAndDate = useCallback(
    async (JobHistory_id) => {
      if (!JobHistory_id) {
        setRecordDate("");
        setTotalCount("");
      }

      const dqm = new DQMClass();
      const response = await dqm.getDQMInfoOnSource(Workspace_id, datasource.name, JobHistory_id);

      if (response.success) {
        setRecordDate(convertTimezone(response.data.recordDate));
        setTotalCount(response.data.resordsCount);
      }
    },
    [Workspace_id, datasource.name]
  );

  useEffect(() => {
    if (IsDataVaultEnabled !== 1) return navigateToHome();
  }, [Workspace_id, IsDataVaultEnabled, navigate, navigateToHome]);

  useEffect(() => {
    setLoading(true);

    const getData = async () => {
      const datasource = new DatasourceClass();
      const result = await datasource.getDatasourcesOnWorkspace(Workspace_id);

      if (result.success) {
        setDatasources(result.data);

        const selectedDs = result.data?.find(({ name }) => name === defaultDs);
        if (selectedDs) setDatasource(selectedDs);
        else setLoadingFields(false);
      }

      setLoading(false);
    };

    getData();
  }, [Workspace_id, defaultDs]);

  useEffect(() => {
    if (selectedHistory) {
      getTotalRecordsAndDate(selectedHistory);
      childRef?.current?.getFields(selectedHistory);
    }
  }, [getTotalRecordsAndDate, selectedHistory]);

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    {
      link: `/my-workspaces/${Workspace_id}`,
      displayName: workspaceName,
      active: false,
    },
    { link: "#", displayName: "Data Quality Dashboard", active: true },
  ];

  return (
    <CommonLayout
      title={
        <>
          Data Quality Dashboard
          <HelpContextIcon screen="DQM Dashboard" />
        </>
      }
      loading={loading}
      breadcrumbArr={breadcrumbArr}
    >
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          pb: 5,
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "22px 30px 30px 30px",
          }}
        >
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <KMKBackButton handleClick={navigateToHome}></KMKBackButton>

              <FormControl
                sx={{
                  width: { xs: "100%", sm: "300px" },
                }}
                variant={"standard"}
                required
              >
                <InputLabel>Select {DATA_SOURCE.singular}</InputLabel>
                <Select
                  className={classes.ifDisabled}
                  value={datasource.name || ""}
                  label="Select dataset"
                  disabled={datasources.length === 0 || loading}
                  onChange={handleDatasetChange}
                >
                  {datasources.map((source) => (
                    <MenuItem key={source.name} value={source.name}>
                      {source.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {datasource && (
                <ButtonUI
                  variant="contained"
                  style={{
                    minWidth: "80px",
                    marginLeft: 15,
                    background: "#0AB2B2",
                  }}
                  onClick={() =>
                    navigate(`/my-workspaces/${Workspace_id}/${navDatasets.link}/statistics`, {
                      state: {
                        Object_id: datasource.id,
                        fromDashboard: true,
                      },
                    })
                  }
                >
                  View Statistics
                </ButtonUI>
              )}

              <ButtonUI
                variant="contained"
                style={{
                  minWidth: "80px",
                  marginLeft: 15,
                  marginRight: "8px",
                  background: "#0AB2B2",
                }}
                onClick={() =>
                  navigate(`/my-workspaces/${Workspace_id}/dqm/summary`, {
                    state: { Name: encodeURIComponent(datasource.name) },
                  })
                }
              >
                View Summary
              </ButtonUI>
            </Grid>
            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginLeft: "auto",
              }}
            >
              <Box className={classes.boxTotalRecords} sx={{ marginRight: "8px", minWidth: "150px" }}>
                Total Records: {totalCount || 0}
              </Box>
              <Box className={classes.boxTotalRecords}>View Data As Of: {recordDate ? `${recordDate}` : " - "}</Box>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <ComponentSuspense>
                <CoverageChart datasource={datasource} loading={loading} />
              </ComponentSuspense>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <ComponentSuspense>
                <FindingsChart
                  findingsCounts={findingsCounts}
                  isDatasetSelected={!!datasource}
                  loading={loadingFields}
                />
              </ComponentSuspense>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <ComponentSuspense>
                <HealthChart
                  Workspace_id={Workspace_id}
                  datasource={datasource.name}
                  poniterClick={handlePointerClick}
                  setLoadingFields={setLoadingFields}
                />
              </ComponentSuspense>
            </Grid>
            {/* <Grid item xs={4}>
              <ComponentSuspense>
                <AlertChart datasource={datasource.name} />
              </ComponentSuspense>
            </Grid> */}
          </Grid>

          <FieldsTable
            Workspace_id={Workspace_id}
            datasource={datasource.name}
            accessRole={accessRole}
            getFindingsCounts={getFindingsCounts}
            loadingFields={loadingFields}
            setLoadingFields={setLoadingFields}
            ref={childRef}
          />
        </Box>
      </Box>
    </CommonLayout>
  );
};

export default DQM;
